import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { SeoManager, seoConfig } from '../seo';
import '../styles/TypingResults.css';
import { getItem } from '../utils/safeStorage';

// Gauge Component: Visualizes a value (CPM) on a semi-circular gauge.
function Gauge({ cpm }) {
  const maxCpm = 500;
  const targetAngle = Math.min((cpm / maxCpm) * 180, 180);
  const [needleAngle, setNeedleAngle] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => setNeedleAngle(targetAngle), 100);
    return () => clearTimeout(timer);
  }, [targetAngle]);

  return (
    <div className="gauge-outer">
      <svg className="gauge-svg" viewBox="0 0 100 50">
        <path d="M 10 50 A 40 40 0 0 1 90 50" stroke="var(--gauge-bg)" strokeWidth="8" fill="none" />
        <path 
          d="M 10 50 A 40 40 0 0 1 90 50" 
          stroke="var(--gauge-accent)" 
          strokeWidth="8" 
          fill="none" 
          strokeDasharray="125.6" 
          strokeDashoffset={125.6 * (1 - Math.min(cpm, maxCpm) / maxCpm)}
          className="gauge-progress"
        />
      </svg>
      <div
        className="gauge-needle"
        style={{ transform: `translateX(-50%) rotate(${needleAngle - 90}deg)` }}
      />
      <div className="gauge-pivot"></div>
    </div>
  );
}

// Rank logic and roast/praise
function getRankInfo(cpm) {
  if (cpm <= 100) {
    return {
      animal: 'Snail',
      message: "Snail speed! Did you take a nap mid-test? 🐌 Even your grandma types faster. (Professions: Beginners, casual typists.)"
    };
  } else if (cpm <= 200) {
    return {
      animal: 'Turtle',
      message: "Turtle mode! 🐢 Slow and steady... but mostly slow. Maybe try using more than one finger? (Professions: Students, occasional typists.)"
    };
  } else if (cpm <= 300) {
    return {
      animal: 'Rabbit',
      message: "Rabbit rank! 🐇 Not bad, but don't get too hoppy. You're faster than a turtle, at least! (Professions: Office workers, data entry clerks.)"
    };
  } else if (cpm <= 400) {
    return {
      animal: 'Wolf',
      message: "Wolf speed! 🐺 You're hunting those keys like a pro. The office pack would be proud. (Professions: Secretaries, administrative assistants.)"
    };
  } else if (cpm <= 500) {
    return {
      animal: 'Cheetah',
      message: "Cheetah level! 🐆 Blazing fast! Are your fingers on fire? (Professions: Professional typists, transcribers.)"
    };
  } else {
    return {
      animal: 'Peregrine Falcon',
      message: "Peregrine Falcon! 🦅 You type faster than most people think! Are you even human? (Professions: Expert typists, competitive speed typists.)"
    };
  }
}

// Helper to get the correct test path from lastTestPath or fallback
function getRetryPath() {
  const lastTestPath = getItem('lastTestPath');
  // Only allow retrying known test paths
  const allowed = [
    '/15s-cpm-typing-test',
    '/30s-cpm-typing-test',
    '/60s-cpm-typing-test',
    '/3min-cpm-typing-test',
    '/5min-cpm-typing-test'
  ];
  if (lastTestPath && allowed.includes(lastTestPath)) {
    return lastTestPath;
  }
  return '/';
}

function readStoredResults() {
  try {
    return JSON.parse(getItem('typingResults') || '{}') || {};
  } catch {
    return {};
  }
}

const TypingResults = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const results = location.state?.testResults || readStoredResults();
  const { animal, message } = getRankInfo(results.cpm || 0);

  const handleTryAgain = () => {
    navigate(getRetryPath());
  };

  return (
    <>
      <SeoManager
        title={seoConfig.results?.title}
        description={seoConfig.results?.description}
        keywords={seoConfig.results?.keywords}
        noindex
      />
    <div className="results-bg">
      <div className="results-card">
        <h2 className="results-title">Typing Test Results</h2>
        <div className="gauge-center">
          <Gauge cpm={results.cpm || 0} />
        </div>
        <div className="rank-row">
          <span className="rank-label">Your Rank:</span>
          <span className="rank-animal">{animal}</span>
        </div>
        <div className="rank-message">{message}</div>
        <div className="results-grid">
          <div className="result-item">
            <h3>WPM</h3>
            <p className="big-number">{results.wpm || 0}</p>
            <p className="label">Words per minute</p>
          </div>
          <div className="result-item">
            <h3>CPM</h3>
            <p className="big-number">{results.cpm || 0}</p>
            <p className="label">Characters per minute</p>
          </div>
          <div className="result-item">
            <h3>Accuracy</h3>
            <p className="big-number">{results.accuracy || 0}%</p>
            <p className="label">Correct characters</p>
          </div>
          <div className="result-item">
            <h3>Mistakes</h3>
            <p className="big-number">{results.mistakes || 0}</p>
            <p className="label">Wrong characters</p>
          </div>
        </div>
        <button className="try-again-btn" onClick={handleTryAgain}>
          Try Again
        </button>
      </div>
    </div>
    </>
  );
};

export default TypingResults; 